import React, { useMemo } from "react";
import { TrendingUp, Target, AlertTriangle, Award } from "lucide-react";
import RevenueChart from "./RevenueChart";
import { normalizeCurrencyCode } from "../../utils/formatters";

const METRICS = [
  { key: "daily", label: "Quotidien" },
  { key: "weekly", label: "Hebdomadaire" },
  { key: "monthly", label: "Mensuel" },
  { key: "annual", label: "Annuel" },
];

const computePercent = (value, target) => {
  if (!target || target <= 0) return 0;
  return Math.min(100, Math.round((value / target) * 100));
};

const sumProgress = (teamProgress = []) =>
  teamProgress.reduce(
    (acc, entry) => {
      const progress = entry.progress || {};
      acc.daily += Number(progress.daily) || 0;
      acc.weekly += Number(progress.weekly) || 0;
      acc.monthly += Number(progress.monthly) || 0;
      acc.annual += Number(progress.annual) || 0;
      acc.total += Number(progress.total) || 0;
      return acc;
    },
    { daily: 0, weekly: 0, monthly: 0, annual: 0, total: 0 }
  );

const RevenueSummary = ({
  isAdmin,
  globalTarget,
  userObjective,
  progress,
  teamProgress,
  latestObservation,
}) => {
  const rawCurrency =
    userObjective?.currency || globalTarget?.currency || "FCFA";
  const currency = normalizeCurrencyCode(rawCurrency, "FCFA");
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
      }),
    [currency]
  );

  const aggregates = useMemo(() => sumProgress(teamProgress), [teamProgress]);

  const objectiveSource = isAdmin ? globalTarget : userObjective;
  const effectiveProgress = isAdmin ? aggregates : progress;

  const cards = useMemo(() => {
    if (isAdmin) {
      return [
        {
          title: "CA réalisé (équipe)",
          value: formatter.format(effectiveProgress.total || 0),
          helper: `Objectif global : ${formatter.format(
            globalTarget?.annualTarget || 0
          )}`,
          icon: TrendingUp,
        },
        {
          title: "Objectif annuel",
          value: formatter.format(globalTarget?.annualTarget || 0),
          helper: `Mois en cours : ${formatter.format(
            globalTarget?.monthlyTarget || 0
          )}`,
          icon: Target,
        },
        {
          title: "Moyenne par commercial",
          value: formatter.format(
            teamProgress.length
              ? (effectiveProgress.total || 0) / teamProgress.length
              : 0
          ),
          helper: `${teamProgress.length} commerciaux suivis`,
          icon: Award,
        },
        {
          title: "Alertes",
          value: `${
            teamProgress.filter(
              (item) =>
                item?.objective?.lastObservationLevel === "warning" ||
                item?.objective?.lastObservationLevel === "danger"
            ).length
          }`,
          helper: "Commentaires à traiter",
          icon: AlertTriangle,
        },
      ];
    }

    return [
      {
        title: "CA réalisé",
        value: formatter.format(effectiveProgress.total || 0),
        helper: `Objectif annuel : ${formatter.format(
          userObjective?.annualTarget || 0
        )}`,
        icon: TrendingUp,
      },
      {
        title: "Objectif mensuel",
        value: formatter.format(userObjective?.monthlyTarget || 0),
        helper: `${computePercent(
          effectiveProgress.monthly,
          userObjective?.monthlyTarget
        )}% atteint`,
        icon: Target,
      },
      {
        title: "Objectif hebdomadaire",
        value: formatter.format(userObjective?.weeklyTarget || 0),
        helper: `${computePercent(
          effectiveProgress.weekly,
          userObjective?.weeklyTarget
        )}% atteint`,
        icon: Target,
      },
      {
        title: "Objectif quotidien",
        value: formatter.format(userObjective?.dailyTarget || 0),
        helper: `${computePercent(
          effectiveProgress.daily,
          userObjective?.dailyTarget
        )}% atteint`,
        icon: Target,
      },
    ];
  }, [
    effectiveProgress,
    formatter,
    globalTarget,
    isAdmin,
    teamProgress,
    userObjective,
  ]);

  return (
    <section className="panel-surface p-6 space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">
            Pilotage du chiffre d'affaires
          </p>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
            {isAdmin ? "Vue globale" : "Votre progression"}
          </h2>
        </div>

        {latestObservation && !isAdmin && (
          <div className="bg-orange-50 border border-orange-200 text-orange-700 px-4 py-2 rounded-lg text-sm">
            <span className="font-semibold">Observation :</span>{" "}
            {latestObservation.message}
          </div>
        )}
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ title, value, helper, icon: Icon }) => (
          <div key={title} className="panel-quiet p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
              <Icon className="w-4 h-4 text-[var(--color-brand)]" />
              <span>{title}</span>
            </div>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">
              {value}
            </p>
            {helper && (
              <p className="text-xs text-[var(--color-text-secondary)]">
                {helper}
              </p>
            )}
          </div>
        ))}
      </div>

      <RevenueChart
        progress={effectiveProgress}
        objective={objectiveSource}
        globalTarget={globalTarget}
        currency={currency}
        title="Projection sur les objectifs"
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {METRICS.map(({ key, label }) => {
          const target = objectiveSource?.[`${key}Target`] || 0;
          const value = effectiveProgress?.[key] || 0;
          const percent = computePercent(value, target);
          return (
            <div key={key} className="panel-quiet p-4 space-y-2">
              <div className="flex items-center justify-between text-sm text-[var(--color-text-secondary)]">
                <span>{label}</span>
                <span>{percent}%</span>
              </div>
              <div className="h-2 rounded-full bg-[var(--color-bg-surface)]">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-sky-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
                <span>{formatter.format(value)}</span>
                <span>{formatter.format(target)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default RevenueSummary;
